import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { invoiceAPI } from "../../services/api";
import type { Invoice, InvoiceState } from "../../types/invoice";

const initialState: InvoiceState = {
  invoices: [],
  current: null,
  clientSummary: null,
  preview: null,
  detailPlans: null,
  invoiceableItems: [],
  itemsLoading: false,
  itemsPagination: { page: 1, limit: 20, total: 0, pages: 0 },
  loading: false,
  saving: false,
  error: null,
  pagination: { page: 1, limit: 20, total: 0, pages: 0 },
};

const err = (e: any, fallback: string) =>
  e.response?.data?.message || fallback;

export const fetchClientSummary = createAsyncThunk(
  "invoices/clientSummary",
  async (clientId: string, { rejectWithValue }) => {
    try {
      const res = await invoiceAPI.getClientSummary(clientId);
      return res.data.data;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load client summary"));
    }
  },
);

export const previewInvoice = createAsyncThunk(
  "invoices/preview",
  async (
    payload: {
      clientId: string;
      shipmentIds: string[];
      workIds: string[];
      /** Containers — the third source; each resolves to a client via its order. */
      containerLineIds?: string[];
      serviceSelections?: Record<string, string[]>;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await invoiceAPI.preview(payload);
      return res.data.data;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to build preview"));
    }
  },
);

export const fetchInvoiceableItems = createAsyncThunk(
  "invoices/invoiceableItems",
  async (
    params: {
      clientId?: string;
      sourceType?: string;
      page?: number;
      limit?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await invoiceAPI.invoiceableItems(params);
      return res.data.data;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load invoiceable items"));
    }
  },
);

export const listInvoices = createAsyncThunk(
  "invoices/list",
  async (params: any, { rejectWithValue }) => {
    try {
      const res = await invoiceAPI.list(params);
      return res.data.data;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to list invoices"));
    }
  },
);

export const getInvoice = createAsyncThunk(
  "invoices/get",
  async (id: string, { rejectWithValue }) => {
    try {
      // The whole payload, not just the invoice: a draft comes back with the
      // description plans its lines would compose (see InvoiceDetailsPayload).
      const res = await invoiceAPI.getOne(id);
      return res.data.data;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load invoice"));
    }
  },
);

export const createInvoice = createAsyncThunk(
  "invoices/create",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await invoiceAPI.create(data);
      return res.data.data.invoice;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to create invoice"));
    }
  },
);

export const updateInvoice = createAsyncThunk(
  "invoices/update",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const res = await invoiceAPI.update(id, data);
      return res.data.data.invoice;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to update invoice"));
    }
  },
);

// The server's error `code` rides on the rejected action's meta, so the editor
// can tell "range used up" apart without `state.error` ceasing to be a string.
export const lockInvoice = createAsyncThunk<
  Invoice,
  { id: string; data: any },
  { rejectValue: string; rejectedMeta: { code?: string } }
>("invoices/lock", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await invoiceAPI.lock(id, data);
    return res.data.data.invoice;
  } catch (e: any) {
    return rejectWithValue(err(e, "Failed to lock invoice"), {
      code: e.response?.data?.code,
    });
  }
});

export const setInvoiceStatus = createAsyncThunk(
  "invoices/setStatus",
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    try {
      const res = await invoiceAPI.setStatus(id, status);
      return res.data.data.invoice;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to update status"));
    }
  },
);

export const cancelInvoice = createAsyncThunk(
  "invoices/cancel",
  async ({ id, reason }: { id: string; reason?: string }, { rejectWithValue }) => {
    try {
      const res = await invoiceAPI.cancel(id, reason);
      return res.data.data.invoice;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to cancel invoice"));
    }
  },
);

export const createCreditNote = createAsyncThunk(
  "invoices/creditNote",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const res = await invoiceAPI.createCreditNote(id, data);
      return res.data.data.creditNote;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to issue credit note"));
    }
  },
);

const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearInvoiceError: (state) => {
      state.error = null;
    },
    clearPreview: (state) => {
      state.preview = null;
    },
    clearCurrent: (state) => {
      state.current = null;
    },
    clearClientSummary: (state) => {
      state.clientSummary = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.clientSummary = action.payload;
      })
      .addCase(fetchClientSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchInvoiceableItems.pending, (state) => {
        state.itemsLoading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceableItems.fulfilled, (state, action) => {
        state.itemsLoading = false;
        state.invoiceableItems = action.payload.items;
        state.itemsPagination = action.payload.pagination;
      })
      .addCase(fetchInvoiceableItems.rejected, (state, action) => {
        state.itemsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(previewInvoice.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(previewInvoice.fulfilled, (state, action) => {
        state.saving = false;
        state.preview = action.payload;
      })
      .addCase(previewInvoice.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })

      .addCase(listInvoices.pending, (state) => {
        state.loading = true;
      })
      .addCase(listInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.invoices;
        state.pagination = action.payload.pagination;
      })
      .addCase(listInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(getInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload.invoice;
        state.detailPlans = action.payload.invoiceDetails ?? null;
      })
      .addCase(getInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Mutations that resolve to an invoice → set as current.
    for (const thunk of [
      createInvoice,
      updateInvoice,
      lockInvoice,
      setInvoiceStatus,
      cancelInvoice,
      createCreditNote,
    ]) {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.saving = false;
          state.current = action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.saving = false;
          state.error = action.payload as string;
        });
    }
  },
});

export const {
  clearInvoiceError,
  clearPreview,
  clearCurrent,
  clearClientSummary,
} = invoicesSlice.actions;
export default invoicesSlice.reducer;
